import React from 'react';
import { motion } from 'framer-motion';

/**
 * ProcessVisual — a custom branded isometric illustration for the
 * "Software Development Process" section. Themed around AI & technology:
 * a glass dashboard with an animated neural network, a live analytics
 * chart, an "AI" engine chip with pulsing nodes, a code window, and a
 * cloud/CI-CD pipeline — all in the Bogin Tech palette (orange #ff6a00 /
 * #d44a00, blue #1438bc / #2a55e8, navy #0B1957) so it reads cleanly on
 * the navy section background. Fully vector, transparent and animated.
 */
const ProcessVisual = () => {
    return (
        <motion.div
            className="bt-process-visual updown"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            aria-hidden="true"
        >
            <svg
                viewBox="0 0 560 620"
                xmlns="http://www.w3.org/2000/svg"
                overflow="visible"
                role="img"
                aria-label="AI-driven software development process illustration"
                style={{ width: '100%', height: 'auto' }}
            >
                <defs>
                    <linearGradient id="pv-platform" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#2a55e8" />
                        <stop offset="100%" stopColor="#1438bc" />
                    </linearGradient>
                    <linearGradient id="pv-glass" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.98" />
                        <stop offset="100%" stopColor="#eef2ff" stopOpacity="0.92" />
                    </linearGradient>
                    <linearGradient id="pv-warm" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#ff8a3d" />
                        <stop offset="100%" stopColor="#d44a00" />
                    </linearGradient>
                    <linearGradient id="pv-chip" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#11227a" />
                        <stop offset="100%" stopColor="#0B1957" />
                    </linearGradient>
                    <linearGradient id="pv-bar" x1="0" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#2a55e8" />
                        <stop offset="100%" stopColor="#6f93ff" />
                    </linearGradient>
                    <filter id="pv-shadow" x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="12" />
                        <feOffset dx="0" dy="16" result="off" />
                        <feComponentTransfer>
                            <feFuncA type="linear" slope="0.22" />
                        </feComponentTransfer>
                        <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Base platform */}
                <ellipse cx="280" cy="500" rx="250" ry="78" fill="url(#pv-platform)" opacity="0.95" />
                <ellipse cx="280" cy="500" rx="250" ry="78" fill="none" stroke="#ffffff" strokeOpacity="0.12" strokeWidth="2" />
                {/* concentric base rings (echo of brand style) */}
                {[0, 1, 2, 3].map((i) => (
                    <ellipse key={i} cx="280" cy={500 + i * 18} rx={250 - i * 6} ry={78 - i * 4}
                        fill="none" stroke="#2a55e8" strokeOpacity={0.35 - i * 0.07} strokeWidth="2" />
                ))}

                {/* ===== Main dashboard panel ===== */}
                <g filter="url(#pv-shadow)">
                    <rect x="120" y="150" width="320" height="220" rx="18" fill="url(#pv-glass)" />
                    {/* header bar */}
                    <rect x="120" y="150" width="320" height="40" rx="18" fill="#0B1957" />
                    <rect x="120" y="172" width="320" height="18" fill="#0B1957" />
                    <circle cx="146" cy="170" r="6" fill="#ff6a00" />
                    <circle cx="166" cy="170" r="6" fill="#2a55e8" />
                    <circle cx="186" cy="170" r="6" fill="#ffffff" opacity="0.7" />
                    <rect x="360" y="163" width="62" height="14" rx="7" fill="#ffffff" opacity="0.18" />

                    {/* neural network graphic */}
                    <g stroke="#cfd8ff" strokeWidth="2">
                        <line x1="175" y1="230" x2="250" y2="215" />
                        <line x1="175" y1="230" x2="250" y2="270" />
                        <line x1="175" y1="300" x2="250" y2="215" />
                        <line x1="175" y1="300" x2="250" y2="270" />
                        <line x1="250" y1="215" x2="330" y2="245" />
                        <line x1="250" y1="270" x2="330" y2="245" />
                    </g>
                    {/* animated active links */}
                    <motion.line x1="175" y1="230" x2="250" y2="215" stroke="#ff6a00" strokeWidth="2.5"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: [0, 1, 0] }}
                        transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 0.4 }} />
                    <motion.line x1="250" y1="215" x2="330" y2="245" stroke="#ff6a00" strokeWidth="2.5"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: [0, 1, 0] }}
                        transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 0.4, delay: 0.5 }} />

                    {[[175, 230], [175, 300], [250, 215], [250, 270], [330, 245]].map(([cx, cy], i) => (
                        <motion.circle key={i} cx={cx} cy={cy} r="9" fill={i === 4 ? '#ff6a00' : '#2a55e8'}
                            animate={{ scale: [1, 1.25, 1] }}
                            transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.25 }}
                            style={{ transformOrigin: `${cx}px ${cy}px` }} />
                    ))}
                </g>

                {/* ===== Floating analytics card ===== */}
                <g filter="url(#pv-shadow)">
                    <rect x="300" y="300" width="170" height="130" rx="16" fill="url(#pv-glass)" />
                    <rect x="320" y="320" width="80" height="10" rx="5" fill="#0B1957" opacity="0.55" />
                    <rect x="320" y="338" width="50" height="8" rx="4" fill="#9aa6d6" />
                    {[
                        { x: 322, h: 34 }, { x: 348, h: 54 }, { x: 374, h: 44 }, { x: 400, h: 70 }, { x: 426, h: 58 },
                    ].map((b, i) => (
                        <motion.rect key={i} x={b.x} width="16" rx="4" fill="url(#pv-bar)"
                            initial={{ height: 0, y: 410 }}
                            animate={{ height: b.h, y: 410 - b.h }}
                            transition={{ duration: 0.9, delay: 0.4 + i * 0.12, ease: 'easeOut' }} />
                    ))}
                </g>

                {/* ===== AI engine chip ===== */}
                <g filter="url(#pv-shadow)">
                    <rect x="70" y="320" width="150" height="120" rx="16" fill="url(#pv-chip)" />
                    {/* chip pins */}
                    {[0, 1, 2, 3].map((i) => (
                        <rect key={`t${i}`} x={92 + i * 30} y="310" width="10" height="12" rx="3" fill="#2a55e8" />
                    ))}
                    {[0, 1, 2, 3].map((i) => (
                        <rect key={`b${i}`} x={92 + i * 30} y="438" width="10" height="12" rx="3" fill="#2a55e8" />
                    ))}
                    <rect x="98" y="348" width="94" height="64" rx="10" fill="#0a1547" stroke="#2a55e8" strokeOpacity="0.5" />
                    <text x="145" y="390" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700"
                        fontSize="30" fill="#ff6a00" letterSpacing="1">AI</text>
                    <motion.rect x="98" y="348" width="94" height="64" rx="10" fill="none" stroke="#ff6a00" strokeWidth="2"
                        animate={{ opacity: [0.2, 0.9, 0.2] }}
                        transition={{ duration: 2, repeat: Infinity }} />
                </g>

                {/* ===== Code window ===== */}
                <g filter="url(#pv-shadow)">
                    <rect x="160" y="392" width="190" height="118" rx="14" fill="#0B1957" />
                    <circle cx="180" cy="412" r="5" fill="#ff6a00" />
                    <circle cx="196" cy="412" r="5" fill="#2a55e8" />
                    <circle cx="212" cy="412" r="5" fill="#ffffff" opacity="0.6" />
                    {[
                        { y: 432, w: 120, c: '#6f93ff' }, { y: 450, w: 86, c: '#ff8a3d' },
                        { y: 468, w: 140, c: '#ffffff' }, { y: 486, w: 70, c: '#6f93ff' },
                    ].map((l, i) => (
                        <motion.rect key={i} x="180" y={l.y} height="7" rx="3.5" fill={l.c} opacity="0.85"
                            initial={{ width: 0 }}
                            animate={{ width: l.w }}
                            transition={{ duration: 0.7, delay: 0.6 + i * 0.18 }} />
                    ))}
                </g>

                {/* ===== Cloud / pipeline badge ===== */}
                <motion.g
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <g filter="url(#pv-shadow)">
                        <rect x="330" y="120" width="120" height="46" rx="23" fill="url(#pv-warm)" />
                        <circle cx="357" cy="143" r="13" fill="#ffffff" opacity="0.95" />
                        <path d="M350 145c0-4 3-7 7-7 2 0 4 1 5 3 4 0 6 3 6 6s-3 5-6 5h-11c-3 0-5-2-5-5 0-2 2-4 4-5z"
                            fill="#ff6a00" />
                        <rect x="378" y="135" width="56" height="8" rx="4" fill="#ffffff" opacity="0.95" />
                        <rect x="378" y="149" width="38" height="7" rx="3.5" fill="#ffffff" opacity="0.7" />
                    </g>
                </motion.g>

                {/* small floating accent tiles */}
                <motion.rect x="92" y="210" width="34" height="34" rx="9" fill="url(#pv-warm)"
                    animate={{ y: [210, 198, 210] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
                <motion.rect x="448" y="250" width="28" height="28" rx="8" fill="#2a55e8"
                    animate={{ y: [250, 262, 250] }}
                    transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }} />
            </svg>
        </motion.div>
    );
};

export default ProcessVisual;
